const STORAGE_PREFIX = "stickyFilters:";
const OWNER_KEY = `${STORAGE_PREFIX}__owner`;

/** Builds the sessionStorage key for a screen's sticky filters. */
export function getStickyFilterStorageKey(screenKey) {
  return `${STORAGE_PREFIX}${screenKey}`;
}

/**
 * Reads persisted filter state for a screen from sessionStorage.
 * Returns null when missing or invalid JSON.
 */
export function loadStickyFilters(screenKey) {
  if (!screenKey || typeof window === "undefined") {
    return null;
  }

  try {
    const raw = sessionStorage.getItem(getStickyFilterStorageKey(screenKey));
    if (!raw) {
      return null;
    }

    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch {
    return null;
  }
}

/** Persists filter state for a screen to sessionStorage. */
export function saveStickyFilters(screenKey, filters) {
  if (!screenKey || typeof window === "undefined" || !filters) {
    return;
  }

  try {
    sessionStorage.setItem(
      getStickyFilterStorageKey(screenKey),
      JSON.stringify(filters)
    );
  } catch {
    // Ignore quota errors or private browsing restrictions.
  }
}

/** Removes persisted filter state for a screen. */
export function clearStickyFilters(screenKey) {
  if (!screenKey || typeof window === "undefined") {
    return;
  }

  try {
    sessionStorage.removeItem(getStickyFilterStorageKey(screenKey));
  } catch {
    // Ignore storage access errors.
  }
}

/** Removes every sticky-filter key (all screens + owner marker). */
export function clearAllStickyFilters() {
  if (typeof window === "undefined") {
    return;
  }

  try {
    const keysToRemove = [];
    for (let i = 0; i < sessionStorage.length; i += 1) {
      const key = sessionStorage.key(i);
      if (key?.startsWith(STORAGE_PREFIX)) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach((key) => sessionStorage.removeItem(key));
  } catch {
    // Ignore storage access errors.
  }
}

/**
 * Binds sticky filters to the signed-in Clerk user.
 * Clears all sticky filters when userId is missing or differs from the stored owner
 * (logout, expired session, or another account signing in on the same tab).
 */
export function syncStickyFiltersOwner(userId) {
  if (typeof window === "undefined") {
    return;
  }

  try {
    if (!userId) {
      clearAllStickyFilters();
      return;
    }

    const currentOwner = sessionStorage.getItem(OWNER_KEY);
    if (currentOwner && currentOwner !== userId) {
      clearAllStickyFilters();
    }

    sessionStorage.setItem(OWNER_KEY, userId);
  } catch {
    // Ignore storage access errors.
  }
}
